    // ─── components/Shared.jsx — reusable bits ───────────────────────────────────
import { useState, useRef, useEffect } from "react";
import { LABEL_COLORS, LABEL_NAMES, checklistProgress, uid } from "../store.js";

// ── colour palette ────────────────────────────────────────────────────────────
export const C = {
  bg:         "#1d2125",
  surface:    "#22272b",
  surfaceHov: "#2d333b",
  border:     "#2d333b",
  border2:    "#373e47",
  borderHov:  "#444c56",
  accent:     "#579dff",
  accentHov:  "#4f8fef",
  text:       "#cdd9e5",
  textMid:    "#b6c2cf",
  textMuted:  "#768390",
  textDim:    "#545d68",
  green:      "#4bce97",
  red:        "#f87168",
  orange:     "#f5a623",
  purple:     "#9f8fef",
};

// ── LabelPill ─────────────────────────────────────────────────────────────────
export function LabelPill({ color, small }) {
  const s = LABEL_COLORS[color] || LABEL_COLORS.gray;
  if (small) return (
    <div style={{ background: s.bg, borderRadius: 3, height: 6, width: 32 }} title={LABEL_NAMES[color]} />
  );
  return (
    <div style={{ background: s.bg, color: s.text, borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700, letterSpacing: "0.03em" }}>
      {LABEL_NAMES[color]}
    </div>
  );
}

// ── ChecklistBar ──────────────────────────────────────────────────────────────
export function ChecklistBar({ pct, compact }) {
  const color = pct === 100 ? C.green : pct > 50 ? C.accent : C.orange;
  if (compact) return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <span style={{ fontSize: 10, color: pct === 100 ? C.green : C.textMuted, fontWeight: 600 }}>✓ {pct}%</span>
      <div style={{ width: 60, height: 3, background: C.border, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 2, transition: "width .3s" }} />
      </div>
    </div>
  );
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMuted, marginBottom: 4 }}>
        <span>Checklist</span>
        <span style={{ fontWeight: 700, color: pct === 100 ? C.green : C.textMid }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 3, transition: "width .3s" }} />
      </div>
    </div>
  );
}

// ── Btn ───────────────────────────────────────────────────────────────────────
export function Btn({ label, onClick, primary, danger, small, icon }) {
  const [hov, setHov] = useState(false);
  let bg = "transparent", border = `1px solid ${C.border2}`, color = hov ? C.textMid : C.textMuted;
  if (primary) { bg = hov ? C.accentHov : C.accent; border = "none"; color = "#fff"; }
  if (danger)  { bg = hov ? "#c0392b" : C.red;       border = "none"; color = "#fff"; }
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov && !primary && !danger ? C.surfaceHov : bg, border, borderRadius: 6, padding: small ? "5px 10px" : "7px 14px", fontSize: small ? 11 : 12, fontWeight: 600, color, cursor: "pointer", fontFamily: "inherit", transition: "all .12s", display: "flex", alignItems: "center", gap: 5 }}>
      {icon && <span>{icon}</span>}{label}
    </button>
  );
}

// ── SidebarBtn (used in modal) ────────────────────────────────────────────────
export function SidebarBtn({ icon, label, onClick, colorVal }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 500, color: hov ? (colorVal || C.text) : (colorVal || C.textMuted), background: hov ? C.surfaceHov : C.surface, marginBottom: 4, transition: "all .12s", border: `1px solid ${hov ? C.borderHov : C.border}` }}>
      <span>{icon}</span>{label}
    </div>
  );
}

// ── AddCardInline ─────────────────────────────────────────────────────────────
export function AddCardInline({ colId, onAdd, onCancel }) {
  const [title, setTitle] = useState("");
  const ref = useRef();
  useEffect(() => { ref.current?.focus(); }, []);

  const submit = () => {
    if (!title.trim()) return onCancel();
    onAdd(colId, title.trim());
    onCancel();
  };

  return (
    <div>
      <textarea ref={ref} value={title} onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } if (e.key === "Escape") onCancel(); }}
        placeholder="Enter a title for this card..."
        style={{ width: "100%", background: C.surface, border: `1px solid ${C.accent}`, borderRadius: 6, padding: "8px 10px", fontSize: 13, color: C.text, resize: "none", outline: "none", fontFamily: "inherit", minHeight: 60, lineHeight: 1.5 }} />
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <Btn label="Add card" onClick={submit} primary />
        <button onClick={onCancel} style={{ background: "transparent", border: "none", fontSize: 22, color: C.textMuted, cursor: "pointer" }}>×</button>
      </div>
    </div>
  );
}

// ── CardModal ─────────────────────────────────────────────────────────────────
export function CardModal({ card, columns, onClose, onUpdate, onArchive, onDelete }) {
  const [title, setTitle]       = useState(card.title);
  const [desc, setDesc]         = useState(card.desc || "");
  const [checklist, setChecklist] = useState(card.checklist || []);
  const [labels, setLabels]     = useState(card.labels || []);
  const [newItem, setNewItem]   = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [showLabels, setShowLabels]   = useState(false);
  const [colId, setColId]       = useState(card.colId);

  const progress = checklistProgress(checklist);

  // auto-save on any change
  useEffect(() => {
    onUpdate({ ...card, title, desc, checklist, labels, colId });
  }, [title, desc, checklist, labels, colId]);

  const addItem = () => {
    if (!newItem.trim()) return;
    setChecklist(p => [...p, { id: uid(), text: newItem.trim(), done: false }]);
    setNewItem("");
  };

  const toggleItem  = id => setChecklist(p => p.map(c => c.id === id ? { ...c, done: !c.done } : c));
  const deleteItem  = id => setChecklist(p => p.filter(c => c.id !== id));
  const updateItem  = (id, text) => setChecklist(p => p.map(c => c.id === id ? { ...c, text } : c));
  const toggleLabel = color => setLabels(p => p.includes(color) ? p.filter(l => l !== color) : [...p, color]);

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.72)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 9999, padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#22272b", borderRadius: 12, width: "100%", maxWidth: 680, boxShadow: "0 20px 60px rgba(0,0,0,.55)" }}>

        {/* ── top ── */}
        <div style={{ padding: "20px 20px 0" }}>
          {labels.length > 0 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
              {labels.map(l => <LabelPill key={l} color={l} />)}
            </div>
          )}
          <textarea value={title} onChange={e => setTitle(e.target.value)}
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", fontSize: 18, fontWeight: 700, color: C.text, resize: "none", fontFamily: "inherit", lineHeight: 1.4 }}
            rows={title.length > 60 ? 2 : 1} />
          <div style={{ fontSize: 12, color: C.textDim, marginBottom: 16 }}>
            Column:{" "}
            <select value={colId} onChange={e => setColId(e.target.value)}
              style={{ background: C.surfaceHov, border: `1px solid ${C.border2}`, borderRadius: 4, color: C.textMid, fontSize: 12, padding: "2px 6px", fontFamily: "inherit" }}>
              {columns.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex" }}>
          {/* ── left ── */}
          <div style={{ flex: 1, padding: "0 20px 24px" }}>
            {/* Description */}
            <Section label="📝 Description">
              <textarea value={desc} onChange={e => setDesc(e.target.value)}
                placeholder="Add a description..."
                style={{ width: "100%", background: C.surfaceHov, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "10px 12px", fontSize: 13, color: C.textMid, resize: "vertical", outline: "none", fontFamily: "inherit", minHeight: 80, lineHeight: 1.6 }}
                onFocus={e => e.target.style.borderColor = C.accent}
                onBlur={e => e.target.style.borderColor = C.border2} />
            </Section>

            {/* Checklist */}
            <Section label="☑ Checklist" right={progress && `${progress.done}/${progress.total}`}>
              {progress && <ChecklistBar pct={progress.pct} />}
              <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 10 }}>
                {checklist.map(item => (
                  <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 5, background: C.bg }}>
                    <input type="checkbox" checked={item.done} onChange={() => toggleItem(item.id)}
                      style={{ accentColor: C.accent, width: 15, height: 15, flexShrink: 0, cursor: "pointer" }} />
                    {editingItem === item.id ? (
                      <input autoFocus value={item.text} onChange={e => updateItem(item.id, e.target.value)}
                        onBlur={() => setEditingItem(null)}
                        onKeyDown={e => (e.key === "Enter" || e.key === "Escape") && setEditingItem(null)}
                        style={{ flex: 1, background: C.surfaceHov, border: `1px solid ${C.accent}`, borderRadius: 4, padding: "3px 8px", fontSize: 13, color: C.text, outline: "none", fontFamily: "inherit" }} />
                    ) : (
                      <span onClick={() => setEditingItem(item.id)}
                        style={{ flex: 1, fontSize: 13, color: item.done ? C.textDim : C.textMid, textDecoration: item.done ? "line-through" : "none", cursor: "text" }}>
                        {item.text}
                      </span>
                    )}
                    <span onClick={() => deleteItem(item.id)} style={{ color: C.textDim, cursor: "pointer", fontSize: 16, padding: "0 4px" }}>×</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input value={newItem} onChange={e => setNewItem(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addItem()}
                  placeholder="Add an item…"
                  style={{ flex: 1, background: C.surfaceHov, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "7px 10px", fontSize: 13, color: C.textMid, outline: "none", fontFamily: "inherit" }}
                  onFocus={e => e.target.style.borderColor = C.accent}
                  onBlur={e => e.target.style.borderColor = C.border2} />
                <Btn label="Add" onClick={addItem} primary small />
              </div>
            </Section>
          </div>

          {/* ── right ── */}
          <div style={{ width: 160, padding: "0 16px 24px 0", flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Actions</div>

            {/* Label picker */}
            <div style={{ position: "relative", marginBottom: 4 }}>
              <SidebarBtn icon="🏷" label="Labels" onClick={() => setShowLabels(p => !p)} />
              {showLabels && (
                <div style={{ position: "absolute", right: 0, top: "100%", zIndex: 10, background: C.surfaceHov, border: `1px solid ${C.borderHov}`, borderRadius: 8, padding: 10, width: 185, boxShadow: "0 8px 24px rgba(0,0,0,.4)" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 8 }}>LABELS</div>
                  {Object.entries(LABEL_COLORS).map(([key]) => (
                    <div key={key} onClick={() => toggleLabel(key)}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", borderRadius: 4, cursor: "pointer", marginBottom: 2, background: labels.includes(key) ? C.border2 : "transparent" }}>
                      <div style={{ width: 28, height: 14, background: LABEL_COLORS[key].bg, borderRadius: 3 }} />
                      <span style={{ fontSize: 12, color: C.textMid }}>{LABEL_NAMES[key]}</span>
                      {labels.includes(key) && <span style={{ marginLeft: "auto", color: C.accent, fontSize: 12 }}>✓</span>}
                    </div>
                  ))}
                  <div onClick={() => setShowLabels(false)} style={{ textAlign: "center", marginTop: 8, fontSize: 12, color: C.textMuted, cursor: "pointer" }}>Close</div>
                </div>
              )}
            </div>

            <SidebarBtn icon={card.archived ? "♻️" : "🗃"} label={card.archived ? "Unarchive" : "Archive"} onClick={() => { onArchive(card.id); onClose(); }} colorVal={card.archived ? C.green : undefined} />
            <SidebarBtn icon="🗑" label="Delete" onClick={() => { if (window.confirm("Delete this card?")) { onDelete(card.id); onClose(); } }} colorVal={C.red} />

            <div style={{ marginTop: 16, fontSize: 11, color: C.textDim, lineHeight: 1.8 }}>
              Created<br />
              <span style={{ color: C.textMuted }}>{new Date(card.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── helper ────────────────────────────────────────────────────────────────────
function Section({ label, right, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        <span>{label}</span>
        {right && <span style={{ color: C.textMid }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}